'use client'

import type { ReactNode } from 'react'
import { ArrowRight, CheckCircle2, Sparkles } from 'lucide-react'
import { cn } from '../../lib/utils'
import { ButtonLink, StatusBadge } from '../ui'
import styles from './OnboardingGuidance.module.css'

type Tone = 'emerald' | 'gold' | 'dark'

type OnboardingAction = {
  href: string
  label: string
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'gold'
}

type OnboardingStep = {
  title: string
  body: string
}

export function OnboardingNudge({
  eyebrow = 'First time guide',
  title,
  body,
  steps = [],
  actions = [],
  tone = 'emerald',
  className,
}: {
  eyebrow?: string
  title: string
  body: string
  steps?: OnboardingStep[]
  actions?: OnboardingAction[]
  tone?: Tone
  className?: string
}) {
  return (
    <aside
      className={cn(
        styles.nudge,
        tone === 'gold' && styles.nudgeGold,
        tone === 'dark' && styles.nudgeDark,
        className,
      )}
    >
      <div className={styles.head}>
        <div>
          <span className={styles.kicker}><Sparkles size={14} /> {eyebrow}</span>
          <h2 className={styles.title}>{title}</h2>
        </div>
        <StatusBadge tone={tone === 'gold' ? 'gold' : tone === 'dark' ? 'info' : 'emerald'}>Guided</StatusBadge>
      </div>
      <p className={styles.body}>{body}</p>
      {steps.length > 0 && (
        <ul className={styles.steps}>
          {steps.map((step) => (
            <li key={step.title}>
              <span className={styles.stepIcon}><CheckCircle2 size={14} /></span>
              <span><strong>{step.title}</strong>{step.body}</span>
            </li>
          ))}
        </ul>
      )}
      {actions.length > 0 && (
        <div className={styles.actions}>
          {actions.map((action, index) => (
            <ButtonLink
              key={action.href + action.label}
              href={action.href}
              variant={action.variant || (index === 0 ? 'primary' : 'secondary')}
              size="sm"
              rightIcon={index === 0 ? <ArrowRight size={14} /> : undefined}
            >
              {action.label}
            </ButtonLink>
          ))}
        </div>
      )}
    </aside>
  )
}

export function RoleStarterChecklist({
  eyebrow = 'Start here',
  title,
  items,
  action,
  className,
}: {
  eyebrow?: string
  title: string
  items: Array<{ title: string; body: string; icon?: ReactNode }>
  action?: ReactNode
  className?: string
}) {
  return (
    <section className={cn(styles.checklist, className)}>
      <div className={styles.checklistHeader}>
        <div>
          <span>{eyebrow}</span>
          <h3>{title}</h3>
        </div>
        {action}
      </div>
      <div className={styles.checklistGrid}>
        {items.map((item) => (
          <div key={item.title} className={styles.checkItem}>
            {item.icon || <CheckCircle2 size={16} />}
            <strong>{item.title}</strong>
            <p>{item.body}</p>
          </div>
        ))}
      </div>
    </section>
  )
}
